// apps/cli/src/lib/prereq-probes.ts
//
// The impure half of `m8t prereqs`: everything that touches Azure. The pure
// evaluators live in prereqs.ts and are unit-tested there; this module is the
// outermost edge and is mocked at that boundary only.

import type { TokenCredential } from "@azure/identity";
import { runAz } from "./az.js";
import { listModelQuota, modelQuotaVerdict } from "./quota.js";
import {
  FOUNDRY_USER_ROLE_ID,
  KV_SECRETS_USER_ROLE_ID,
  grantFoundryUser,
  grantKeyVaultSecretsUser,
  resolveKeyVaultResourceId,
} from "./rbac.js";
import type { AccessProbe, ProviderState, QuotaEvaluation, RedirectUriProbe, SoftDeletedAccount, SoftDeletedEvaluation } from "./prereqs.js";

const FOUNDRY_SCOPE = "https://ai.azure.com/.default";
const KV_SCOPE = "https://vault.azure.net/.default";
const AGENTS_API = "v1";

/**
 * Cognitive Services User. Recognised as a data-plane grant when inspecting
 * SOMEONE ELSE's assignments, because the gateway and updater identities have
 * historically been granted it and it does work — but it is NOT what we grant.
 * Microsoft's own guidance is explicit that Cognitive Services roles are for AI
 * Services resources rather than Foundry projects; Foundry User is the
 * designated role, so `--fix` grants Foundry User and nothing else.
 */
const COGNITIVE_SERVICES_USER_ROLE_ID = "a97b65f3-24c7-4388-baec-2e87135dc908";

const FOUNDRY_DATA_PLANE_ROLE_IDS = [FOUNDRY_USER_ROLE_ID, COGNITIVE_SERVICES_USER_ROLE_ID];

// ─── install phase ──────────────────────────────────────────────────────────

/** Read each namespace's registrationState. A failed read yields `state: null` (unverifiable, not "missing"). */
export async function probeProviders(namespaces: readonly string[], subscriptionId?: string): Promise<ProviderState[]> {
  return Promise.all(
    namespaces.map(async (namespace): Promise<ProviderState> => {
      try {
        const args = ["provider", "show", "--namespace", namespace, "--query", "registrationState", "-o", "tsv"];
        if (subscriptionId) args.push("--subscription", subscriptionId);
        const out = (await runAz(args)).trim();
        // `az` prints the literal "None" for a null tsv value.
        return { namespace, state: out && out !== "None" ? out : null };
      } catch {
        return { namespace, state: null };
      }
    }),
  );
}

/**
 * Register the given namespaces and poll each to `Registered`. Best-effort per
 * namespace: a namespace we cannot register is reported by the follow-up probe
 * as still-unregistered, which is where the refusal + remedy comes from.
 */
export async function registerProviders(
  namespaces: readonly string[],
  opts: { subscriptionId?: string; attempts?: number; sleepMs?: number; onProgress?: (m: string) => void } = {},
): Promise<void> {
  const attempts = opts.attempts ?? 20;
  const sleepMs = opts.sleepMs ?? 5000;
  for (const namespace of namespaces) {
    try {
      const args = ["provider", "register", "--namespace", namespace, "--only-show-errors"];
      if (opts.subscriptionId) args.push("--subscription", opts.subscriptionId);
      await runAz(args);
    } catch {
      continue; // the re-probe reports it; no silent success
    }
    for (let i = 1; i <= attempts; i++) {
      const [state] = await probeProviders([namespace], opts.subscriptionId);
      if (state.state?.toLowerCase() === "registered") break;
      opts.onProgress?.(`registering ${namespace} (${i.toString()}/${attempts.toString()})…`);
      if (sleepMs > 0) await new Promise<void>((r) => setTimeout(r, sleepMs));
    }
  }
}

export async function probeModelQuota(region: string, model: string): Promise<QuotaEvaluation> {
  const usages = await listModelQuota(region);
  const { verdict, quotad } = modelQuotaVerdict(usages, model);
  return { verdict, quotad, model, region, usagesReadable: usages.length > 0 };
}

/** One line of an az failure, capped — enough to act on, short enough to render in a row. */
function failureReason(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const line = raw.split("\n").find((l) => l.trim().length > 0)?.trim() ?? "unknown error";
  return line.length > 300 ? `${line.slice(0, 297)}…` : line;
}

/**
 * Every soft-deleted Cognitive Services account in the subscription — one ARM
 * read. Subscription-wide by design: the caller's region decides how the result
 * is JUDGED, never whether it is read, so plain `m8t prereqs` (before any region
 * is chosen) still has something true to say.
 *
 * A failed or unparseable read is `readable: false` WITH its reason, never an
 * empty list: "no accounts" and "we could not tell" are different sentences and
 * only one of them is safe to report as a pass.
 */
export async function probeSoftDeletedAccounts(subscriptionId?: string): Promise<SoftDeletedEvaluation> {
  const args = ["cognitiveservices", "account", "list-deleted", "-o", "json"];
  if (subscriptionId) args.push("--subscription", subscriptionId);
  let out: string;
  try {
    out = await runAz(args);
  } catch (e) {
    return { readable: false, accounts: [], reason: failureReason(e) };
  }
  try {
    const raw = JSON.parse(out) as { id?: string; name?: string; location?: string; kind?: string; resourceGroup?: string }[];
    // The deleted-account id nests both region and group:
    //   …/locations/<loc>/resourceGroups/<rg>/deletedAccounts/<name>
    // so it is the fallback for each. A missing LOCATION especially must not
    // become "" — an empty region matches no target region, which would report a
    // clean pass for an account we did read. That is the exact false pass this
    // check exists to prevent.
    const fromId = (id: string, segment: string): string | null =>
      new RegExp(`/${segment}/([^/]+)`, "i").exec(id)?.[1] ?? null;
    const accounts: SoftDeletedAccount[] = [];
    for (const a of raw) {
      const id = a.id ?? "";
      const name = a.name ?? fromId(id, "deletedAccounts");
      const location = a.location ?? fromId(id, "locations");
      // An entry we can neither name nor place cannot be judged against a region
      // and cannot have a purge command written for it. Skipping it quietly would
      // let the row report a clean pass over an account we actually read — the
      // exact false pass this check exists to prevent — so the whole read is
      // reported unreadable instead.
      if (!name || !location) {
        return { readable: false, accounts: [], reason: `a deleted-account entry carried no usable name or location (id: ${id || "absent"})` };
      }
      accounts.push({
        name,
        location,
        // Kind decides whether the account can hold MODEL quota at all. A
        // soft-deleted SpeechServices account holds none; refusing on it would
        // block the install and point a purge command at an unrelated resource.
        kind: a.kind ?? "",
        resourceGroup: a.resourceGroup ?? fromId(id, "resourceGroups"),
      });
    }
    return { readable: true, accounts };
  } catch (e) {
    return { readable: false, accounts: [], reason: failureReason(e) };
  }
}

// ─── usage phase ────────────────────────────────────────────────────────────

async function bearer(credential: TokenCredential, scope: string): Promise<string | null> {
  try {
    const t = await credential.getToken(scope);
    return t?.token ?? null;
  } catch {
    return null;
  }
}

/** True when `principalId` holds any recognised Foundry data-plane role at `scope`. */
async function hasRoleAtScope(
  credential: TokenCredential,
  scope: string,
  principalId: string,
  roleIds: readonly string[],
): Promise<boolean | null> {
  try {
    const token = await bearer(credential, "https://management.azure.com/.default");
    if (!token) return null;
    const url =
      `https://management.azure.com${scope}/providers/Microsoft.Authorization/roleAssignments` +
      `?api-version=2022-04-01&$filter=${encodeURIComponent(`principalId eq '${principalId}'`)}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const body = (await res.json()) as { value?: { properties?: { roleDefinitionId?: string } }[] };
    return (body.value ?? []).some((a) =>
      roleIds.some((id) => (a.properties?.roleDefinitionId ?? "").toLowerCase().endsWith(id.toLowerCase())),
    );
  } catch {
    return null;
  }
}

/**
 * Foundry data-plane access.
 *
 * For the caller themselves we CALL the data plane and read the real answer —
 * the same `agents` read the workspace roster makes, so a pass here means the
 * roster loads. For anyone else we can only inspect role assignments; the
 * evaluator's copy says which evidence it used.
 */
export async function probeFoundryAccess(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  accountId: string | null;
  principalId: string | null;
  isSelf: boolean;
}): Promise<AccessProbe> {
  const base: Pick<AccessProbe, "scope" | "principalId" | "isSelf"> = {
    scope: args.accountId,
    principalId: args.principalId,
    isSelf: args.isSelf,
  };
  if (args.isSelf) {
    const token = await bearer(args.credential, FOUNDRY_SCOPE);
    if (!token) return { ...base, evidence: "unavailable" };
    try {
      const res = await fetch(`${args.projectEndpoint}/agents?api-version=${AGENTS_API}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { ...base, evidence: "probe", status: res.status };
    } catch {
      return { ...base, evidence: "unavailable" };
    }
  }
  if (!args.accountId || !args.principalId) return { ...base, evidence: "unavailable" };
  const granted = await hasRoleAtScope(args.credential, args.accountId, args.principalId, FOUNDRY_DATA_PLANE_ROLE_IDS);
  if (granted === null) return { ...base, evidence: "unavailable" };
  return { ...base, evidence: "assignment", granted };
}

/** Key Vault secrets access. Same self-probe / other-inspect split as Foundry. */
export async function probeKeyVaultAccess(args: {
  credential: TokenCredential;
  kvUri: string | null;
  kvResourceId: string | null;
  principalId: string | null;
  isSelf: boolean;
}): Promise<AccessProbe> {
  const base: Pick<AccessProbe, "scope" | "principalId" | "isSelf"> = {
    scope: args.kvResourceId ?? args.kvUri,
    principalId: args.principalId,
    isSelf: args.isSelf,
  };
  if (args.isSelf) {
    if (!args.kvUri) return { ...base, evidence: "unavailable" };
    const token = await bearer(args.credential, KV_SCOPE);
    if (!token) return { ...base, evidence: "unavailable" };
    try {
      const url = `${args.kvUri.replace(/\/$/, "")}/secrets?api-version=7.4&maxresults=1`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      return { ...base, evidence: "probe", status: res.status };
    } catch {
      return { ...base, evidence: "unavailable" };
    }
  }
  if (!args.kvResourceId || !args.principalId) return { ...base, evidence: "unavailable" };
  const granted = await hasRoleAtScope(args.credential, args.kvResourceId, args.principalId, [KV_SECRETS_USER_ROLE_ID]);
  if (granted === null) return { ...base, evidence: "unavailable" };
  return { ...base, evidence: "assignment", granted };
}

export async function probeRedirectUri(clientId: string | null, gatewayUrl: string | null): Promise<RedirectUriProbe> {
  if (!clientId) return { registeredUris: null, gatewayUrl, clientId };
  try {
    const out = await runAz(["ad", "app", "show", "--id", clientId, "--query", "spa.redirectUris", "-o", "json"]);
    return { registeredUris: JSON.parse(out) as string[], gatewayUrl, clientId };
  } catch {
    return { registeredUris: null, gatewayUrl, clientId };
  }
}

/** Resolve the Foundry account ARM id from a project endpoint. Null when it cannot be resolved. */
export async function resolveFoundryAccountId(projectEndpoint: string): Promise<string | null> {
  const m = /^https:\/\/([^.]+)\./.exec(projectEndpoint);
  if (!m) return null;
  try {
    const out = await runAz(["cognitiveservices", "account", "list", "--query", `[?name=='${m[1]}'].id`, "-o", "json"]);
    return (JSON.parse(out) as string[])[0] ?? null;
  } catch {
    return null;
  }
}

/** Resolve the platform Key Vault (uri + ARM id) from the deployment's resource group. */
export async function resolvePlatformKeyVault(
  resourceGroup: string,
  subscriptionId?: string,
): Promise<{ uri: string; id: string } | null> {
  try {
    const args = ["keyvault", "list", "-g", resourceGroup, "--query", "[0].{uri:properties.vaultUri,id:id}", "-o", "json"];
    if (subscriptionId) args.push("--subscription", subscriptionId);
    const parsed = JSON.parse(await runAz(args)) as { uri?: string; id?: string } | null;
    return parsed?.uri && parsed.id ? { uri: parsed.uri, id: parsed.id } : null;
  } catch {
    return null;
  }
}

/** Resolve an object id from a UPN, an object id, or a service principal appId. */
export async function resolvePrincipalObjectId(who: string): Promise<string | null> {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(who)) {
    // Already an object id — but it could also be an appId, so confirm it resolves as a user first.
    const asUser = await runAz(["ad", "user", "show", "--id", who, "--query", "id", "-o", "tsv"]).catch(() => "");
    if (asUser.trim()) return asUser.trim();
    return who;
  }
  const out = await runAz(["ad", "user", "show", "--id", who, "--query", "id", "-o", "tsv"]).catch(() => "");
  return out.trim() || null;
}

// ─── fixes ──────────────────────────────────────────────────────────────────

export async function fixFoundryAccess(args: {
  credential: TokenCredential;
  subscriptionId: string;
  accountId: string;
  principalId: string;
}): Promise<void> {
  await grantFoundryUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    accountScope: args.accountId,
    principalId: args.principalId,
    principalType: "User",
  });
}

export async function fixKeyVaultAccess(args: {
  credential: TokenCredential;
  subscriptionId: string;
  kvResourceId: string;
  principalId: string;
}): Promise<void> {
  await grantKeyVaultSecretsUser({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    kvScope: args.kvResourceId,
    principalId: args.principalId,
    principalType: "User",
  });
}

export { resolveKeyVaultResourceId };
